import { Core } from "@faire/design-tokens";
import { trackRetailersFilterInteraction } from "@faire/web-api/events/retailers/actionUnknown/filterInteraction";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { LoadingSkeleton } from "@faire/web/slate/LoadingSkeleton";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

import { Sort } from "@faire/retailer/components/Filters/Sort";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";

import { FilterName } from "./FilterName";
import { useFilterScrollToProducts } from "./useFilterScrollToProducts";

interface IProps {
  name: FilterName;
  resultsCount: string;
  isLoading?: boolean;
  disableHideShow?: boolean;
  title?: ReactNode;
  className?: string;
  divRef?: React.RefObject<HTMLDivElement>;
  shouldShowTightenedFilters?: boolean;
  titleAlignMargin?: ResponsiveValues<number>;
}

const FilterInfoRowBase: React.FC<IProps> = ({
  name,
  resultsCount,
  isLoading,
  disableHideShow,
  title,
  className,
  divRef,
  shouldShowTightenedFilters,
  titleAlignMargin = { mobileAndAbove: 6, desktopAndAbove: 13 },
}) => {
  const {
    data: { activeFilterCount, isSideBarVisible },
    setFilterState,
  } = useFilterStore(["activeFilterCount", "isSideBarVisible"]);
  const { isTabletAndBelow, isDesktopAndAbove } = useViewport();
  const isDiscoverPage = useSearchPageIsFromDiscoverURL();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  useFilterScrollToProducts(divRef);

  const showResultCount = !shouldShowTightenedFilters && !isLoading;
  const shouldShowSkeleton =
    !shouldShowTightenedFilters && isLoading && isDesktopAndAbove;

  const hasFilterSelected = activeFilterCount > 0;
  const renderFilterToggleButton =
    !shouldShowTightenedFilters &&
    !disableHideShow &&
    activeFilterCount > -1 &&
    isDesktopAndAbove &&
    !isLoggedInRetailer &&
    !isDiscoverPage;

  const onToggleClick = () => {
    trackRetailersFilterInteraction(
      name,
      isSideBarVisible ? "close_filters" : "open_filters",
      "sidebar"
    );
    setFilterState((currentState) => ({
      ...currentState,
      isSideBarVisible: !isSideBarVisible,
      isNewFilterSelected: false,
    }));
  };

  return (
    <>
      <Wrapper className={className} ref={divRef}>
        <StyledRow align="baseline">
          {title ? <Column>{title}</Column> : null}
          <ResultsWrapper $forceSkeletonWidth={!shouldShowTightenedFilters}>
            {showResultCount ? (
              <StyledText
                $titleAlignMargin={titleAlignMargin}
                color={Core.text.subdued}
              >
                {!isLoading && resultsCount !== "" ? (
                  <StrictLocalMsg
                    defaultMessage="{resultsCountNumber} results"
                    description={{
                      text: "Show number of results to the user",
                    }}
                    values={{
                      resultsCountNumber: resultsCount,
                    }}
                  />
                ) : null}
                {isLoading && isTabletAndBelow ? (
                  <LoadingSpinner color={Core.text.subdued} size="small" />
                ) : null}
              </StyledText>
            ) : null}
            {shouldShowSkeleton ? (
              <LoadingSkeleton width="100px" height="16px" />
            ) : null}
          </ResultsWrapper>
          {renderFilterToggleButton ? (
            <HideShowWrapper>
              <StyledText
                $titleAlignMargin={titleAlignMargin}
                variant="paragraphSansRegular"
              >
                <Button variant="tertiary" onClick={onToggleClick}>
                  {isSideBarVisible ? (
                    <StrictLocalMsg
                      defaultMessage="Hide filters"
                      description={{
                        text: "Button text to hide the filter side bar",
                      }}
                    />
                  ) : (
                    <>
                      <StrictLocalMsg
                        defaultMessage="Show filters"
                        description={{
                          text: "Button text to show the filter side bar",
                        }}
                      />
                      {hasFilterSelected ? ` (${activeFilterCount})` : null}
                    </>
                  )}
                </Button>
              </StyledText>
            </HideShowWrapper>
          ) : null}
          <StyledColumn>
            <Sort name={name} />
          </StyledColumn>
        </StyledRow>
      </Wrapper>
    </>
  );
};

export const FilterInfoRow = observer(FilterInfoRowBase);

const ResultsWrapper = styled(Row)<{
  $forceSkeletonWidth?: boolean;
}>`
  min-width: ${({ $forceSkeletonWidth }) =>
    $forceSkeletonWidth ? "100px" : undefined};
`;

const StyledText = styled(Typography)<{
  $titleAlignMargin: ResponsiveValues<number>;
}>`
  ${(props) =>
    css`
      &&& {
        margin-top: ${props.$titleAlignMargin.desktopAndAbove + "px"};
        ${responsiveMediaQueries.tabletAndBelow} {
          margin-top: ${props.$titleAlignMargin.mobileAndAbove + "px"};
        }
      }
    `}
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const StyledColumn = styled(Column)`
  height: 100%;
  margin-left: auto;
  &&& {
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
    &:empty {
      display: none;
    }
  }
`;

const StyledRow = styled(Row)`
  height: 100%;
`;

const Wrapper = styled(Column)`
  height: 100%;
  margin-left: ${getSpacing("2x")};
  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      flex-grow: 1;
    }
  }
`;

const HideShowWrapper = styled.div`
  margin-left: ${getSpacing("2x")};
  ${responsiveMediaQueries.tabletAndBelow} {
    display: none;
  }
`;
